import { useState, useEffect } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';

/** The same activity counted inside the requested `from`/`to` window. */
export interface MonitoringWindow {
  new_users: number | null;
  going_clicks: number | null;
  venue_hours: number | null;
}

export interface MonitoringStats {
  user_count: number | null;
  /** All time. */
  going_clicks: number | null;
  /** Trailing 24 hours. */
  going_clicks_today: number | null;
  /** Trailing 7 days. */
  going_clicks_this_week: number | null;
  venue_hours: number | null;
  instagram_story_tags: number | null;
  estimated_monthly_cost: number | null;
  /** Null when no window was requested, or the backend predates windows. */
  window: MonitoringWindow | null;
}

const EMPTY: MonitoringStats = {
  user_count: null,
  going_clicks: null,
  going_clicks_today: null,
  going_clicks_this_week: null,
  venue_hours: null,
  instagram_story_tags: null,
  estimated_monthly_cost: null,
  window: null,
};

type MonitoringPayload = Partial<MonitoringStats> & { going_clicks_all_time?: number | null };

/**
 * Key Metrics for the monitoring tab, from the stage `env` selects.
 *
 * Routed through `useAdminApi` rather than a hand-built fetch: this hook read
 * REACT_APP_API_URL, null-checked it, and then fetched from
 * REACT_APP_DEV_API_URL regardless — so the headline numbers were dev while the
 * campaign counts beside them were prod. The shared helper also supplies the
 * WAF dev-key header for non-prod, which the hand-built headers never did.
 */
export function useMonitoringStats(
  env: Env,
  from?: string | null,
  to?: string | null,
): {
  data: MonitoringStats;
  loading: boolean;
  error: string | null;
} {
  const { request } = useAdminApi();
  const [data, setData] = useState<MonitoringStats>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    (async () => {
      try {
        const json = await request<MonitoringPayload>({
          env,
          path: '/admin/monitoring/user-count',
          query: { from: from ?? undefined, to: to ?? undefined },
        });
        if (!isMounted) return;
        setData({
          user_count: json.user_count ?? null,
          going_clicks: json.going_clicks_all_time ?? null,
          going_clicks_today: json.going_clicks_today ?? null,
          going_clicks_this_week: json.going_clicks_this_week ?? null,
          venue_hours: json.venue_hours ?? null,
          instagram_story_tags: json.instagram_story_tags ?? null,
          estimated_monthly_cost: json.estimated_monthly_cost ?? null,
          window: json.window ?? null,
        });
        setError(null);
      } catch (err: unknown) {
        if (!isMounted) return;
        // Stale numbers from the previous env would read as this one's.
        setData(EMPTY);
        setError(err instanceof ApiError ? err.message : 'Failed to load monitoring stats');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [request, env, from, to]);

  return { data, loading, error };
}
